import { useRef, useState } from 'react';
import { makeStyles, Menu, MenuItem, IconButton, Tooltip, TextField } from "@material-ui/core";
import { useDispatch, useSelector } from "react-redux";
import { updateMarker, updateSettings } from '../../../store/actions/creator/analysis';
import { useStylesTooltip } from '../../../config/theme';

import InputMask from 'react-input-mask';
import ImmersionModal from '../modals/commonModal';
import UtilService from '../../../helper/service';
import { fetchSessionMarker } from '../../../store/actions/creator/creatorExperienceManager';
import { useFeature } from 'flagged';
import lockIcon from '@material-ui/icons/Lock';
import Icon from '@material-ui/core/Icon';

const useStyles = makeStyles(() => ({
  btn: {
    fontSize: 14,
    fontFamily: 'roboto-regular',
    color: '#697683',
    textTransform: 'unset',
    marginRight: 20,
    marginLeft: 20,
    width: '100%',
    display: 'flex',
    alignItems: 'center'
  },
  menuItem: {
    padding: 0,
    '& .MuiPaper-root': {
      textDecoration: 'none'
    }
  },
  iconBtn: {
    width: 40,
    borderRadius: 10,
    '&:hover': {
      background: '#017AEF14'
    },
  },
  textField: {
    marginTop: -20,
    border: '1px solid grey',
    padding: '8px 12px',
    borderRadius: 4,
    maxWidth: (props) => props ? 125 : 1000,
    width: (props) => props ? 'auto' : '100%',
    background: 'transparent',
    fontFamily: 'roboto-light',
    fontSize: 20,
    color: '#017AEF',
    '& .MuiInputBase-input': {
      fontFamily: 'roboto-light',
      fontSize: 20,
      color: '#017AEF',
    }
  },
  startBox: {
    width: '100%'
  },
  label: {
    marginRight: 30,
    "&$focusedLabel": {
      fontSize: 12,
      fontFamily: 'roboto-bold',
      color: "#606B76"
    },
    "&$erroredLabel": {
      color: "#707070"
    }
  },
  underline: {
    fontSize: 14,
    fontFamily: 'roboto-regular',
    color: '#697683',
    "&$error:after": {
      borderBottomColor: "#017AEF"
    },
    "&:after": {
      borderBottom: `3px solid #017AEF`
    }
  },
  row: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between'
  },
  modalContainer: {
    marginRight: 45
  }
}));

function AnalyzeManualNoteBtn() {

  const hasFeatureNote = useFeature('feature_annotate');

  const tooltipClasses = useStylesTooltip();
  const dispatch = useDispatch();
  const noteRef = useRef(null);
  const { settings, currentSession, analyseStart, analyseEnd } = useSelector((state: any) => state.analysis);
  const { session_code, session_status } = currentSession;
  const isManualNote = session_status === "COMPLETED";
  const classes = useStyles(isManualNote);

  const { pencil } = settings;
  const apolloClient = useSelector((state: any) => state.apolloClient);
  const loginUserData = localStorage.getItem("user_data") ? JSON.parse(localStorage.getItem("user_data") || "{}") : "";

  const [openPencilMenu, setOpenPencilMenu] = useState(false);
  const [openManualModal, setOpenManualModal] = useState(false);
  const [startTime, setStartTime] = useState('00:00:00');
  const [endTime, setEndTime] = useState('00:00:00');
  const [title, setTitle] = useState('');

  const cTime = UtilService.getTimeInputLabel(startTime, false) / 15;
  const eTime = UtilService.getTimeInputLabel(endTime, false) / 15;

  const handleNoteType = (type: number) => {
    setOpenPencilMenu(false)
    if (type === 0) {
      dispatch(updateSettings('pencil', !pencil))
    } else {
      setOpenManualModal(true)
    }
  };

  const onChangeTitle = (event: any) => {
    setTitle(event.target.value);
  };

  const onClose = () => {
    setOpenManualModal(false)
    setStartTime('00:00:00')
    setEndTime('00:00:00')
    setTitle('')
  }

  const onAddNote = () => {
    const request: any = {
      session_code,
      marker_title: title,
      marker_start_at: Math.floor(analyseStart +  cTime ).toString(),
      marker_end_at: Math.floor(analyseStart +  (isManualNote ? eTime : cTime + 1) ).toString(),
      marker_type: 3,
      user_id: parseInt(loginUserData.user_id),
    };
    dispatch(updateMarker(apolloClient.client, request, () => {
      dispatch(fetchSessionMarker(apolloClient.client,  { session_code }, null));
      onClose();
    }))
  }

  return (
    <>
      <Tooltip classes={tooltipClasses} title="Add a Note" placement="bottom" arrow>
        <IconButton
          ref={noteRef}
          className={classes.iconBtn}
          color="primary"
          size='small'
          disableRipple
          onClick={() => {
            if (pencil) {
              dispatch(updateSettings('pencil', false))
            }
            setOpenPencilMenu(true)
          }}
        >
          {!pencil && <img src={'/assets/icons/liveanalysis/cut-edit.svg'} alt='img' />}
          {pencil && <img src={'/assets/icons/liveanalysis/pencil-ruler-blue.svg'} alt='img' />}
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={noteRef.current}
        elevation={1}
        onClose={() => setOpenPencilMenu(false)}
        open={openPencilMenu}
        PaperProps={{
          style: { width: 'auto', marginTop: 50, borderRadius: 12 }
        }}
      >
        {['Draggable Note', 'Manual Note'].map((option, index) => (
          ( hasFeatureNote ? 
          <MenuItem
            className={classes.menuItem}
            key={option}
            onClick={() => handleNoteType(index)}
          >
            <p className={classes.btn}>{option}</p>
          </MenuItem>
          :
          <MenuItem
          className={classes.menuItem}
          key={option}
        >
          <Icon component={lockIcon} color="primary"/>
          <p className={classes.btn}>{option}</p>
        </MenuItem>
          )
        ))}
      </Menu>

      <ImmersionModal
        open={openManualModal}
        title={'Add a Note'}
        titleOK={'Save'}
        disabled={
          !isManualNote 
            ? (!title || cTime > analyseEnd - analyseStart - 5)
            : (!title || cTime > analyseEnd - analyseStart - 5 || eTime - cTime < 5 || eTime > analyseEnd - analyseStart)
        }
        // cancelDisable={true}
        onClose={onClose}
        additional={
          <div className={classes.modalContainer}>
            <br/>
            <div className={classes.row}>
              <div className={classes.startBox}>
                <p>Start Time</p>
                <InputMask
                  value={startTime}
                  onChange={(e: any) => setStartTime(e.target.value)}
                  className={classes.textField}
                  mask="99:99:99"
                  maskChar=" "
                />
              </div>

              {isManualNote && <div>
                <p>End Time</p>
                <InputMask
                  value={endTime}
                  onChange={(e: any) => setEndTime(e.target.value)}
                  className={classes.textField}
                  mask="99:99:99"
                  maskChar=" "
                />
              </div>}
            </div>

            <TextField
              InputLabelProps={{
                classes: {
                  root: classes.label,
                },
              }}
              InputProps={{
                classes: {
                  root: classes.underline,
                }
              }}
              value={title}
              fullWidth
              margin="normal"
              id="project_name"
              label="Note Title"
              name="project_name"
              onChange={onChangeTitle}
            />
          </div>
        }
        onApply={onAddNote}
      />
    </>
  )
}

export default AnalyzeManualNoteBtn;